import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import TopNav from './TopNav';
import Footer from './Footer';
import { ArrowLeft, Clock, User, Calendar } from 'lucide-react';

interface BlogLayoutProps {
  children: React.ReactNode;
  title: string;
  publishDate: string;
  author: string;
  readTime: string;
}

export default function BlogLayout({ children, title, publishDate, author, readTime }: BlogLayoutProps) {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (docHeight > 0) setProgress(Math.min(100, (scrollTop / docHeight) * 100));
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <TopNav />

      {/* Reading progress bar */}
      <div className="fixed top-16 left-0 right-0 z-40 h-0.5 bg-slate-100">
        <div
          className="h-full bg-brand-500 transition-[width] duration-100 ease-out"
          style={{ width: `${progress}%` }}
        />
      </div>

      <article className="max-w-3xl mx-auto px-4 pt-8 pb-16">
        {/* Breadcrumb */}
        <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-brand-600 transition-colors mb-6 group">
          <ArrowLeft className="w-3.5 h-3.5 group-hover:-translate-x-0.5 transition-transform" />
          Back to Blog
        </Link>

        {/* Article header */}
        <header className="mb-10">
          <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 leading-tight mb-5">{title}</h1>
          <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500">
            <span className="inline-flex items-center gap-1.5">
              <div className="w-7 h-7 rounded-full bg-brand-100 flex items-center justify-center">
                <User className="w-3.5 h-3.5 text-brand-700" />
              </div>
              {author}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5" />
              <time>{publishDate}</time>
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5" />
              {readTime} read
            </span>
          </div>
        </header>

        {/* Divider */}
        <div className="w-16 h-1 bg-brand-500 rounded-full mb-10" />

        {/* Article body */}
        <div className="prose prose-slate prose-lg max-w-none
          prose-headings:text-slate-900 prose-headings:font-bold
          prose-a:text-brand-600 prose-a:no-underline hover:prose-a:underline
          prose-strong:text-slate-900
          prose-table:text-sm
          prose-th:bg-slate-50 prose-th:font-semibold
          prose-td:border-slate-100
          prose-img:rounded-xl prose-img:shadow-sm
          prose-hr:border-slate-200
          prose-blockquote:border-brand-300 prose-blockquote:bg-slate-50 prose-blockquote:rounded-r-xl prose-blockquote:py-1
        ">
          {children}
        </div>

        {/* CTA box */}
        <div className="mt-14 relative overflow-hidden rounded-2xl bg-gradient-to-br from-brand-600 to-brand-700 p-8 text-center shadow-lg shadow-brand-600/10">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2240%22%20height%3D%2240%22%20viewBox%3D%220%200%2040%2040%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22%23fff%22%20fill-opacity%3D%220.05%22%3E%3Cpath%20d%3D%22M0%200h20v20H0z%22/%3E%3Cpath%20d%3D%22M20%2020h20v20H20z%22/%3E%3C/g%3E%3C/svg%3E')] opacity-50" />
          <div className="relative">
            <h3 className="text-xl font-bold text-white mb-2">Compare Backlink Prices Free</h3>
            <p className="text-brand-100 mb-5 text-sm">Search any domain and see prices from 50+ marketplaces side by side.</p>
            <Link to="/" className="inline-flex items-center px-6 py-3 bg-white text-brand-700 font-semibold rounded-xl hover:bg-brand-50 transition-colors shadow-sm">
              Try LinkDiry Free
            </Link>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
}
